// FocusPage — the step-based, customer-facing entry surface.
//
// A wizard that funnels into the live refine chat:
//
//   pick project → [ Create | Refine ]
//        Create → "where in the codebase" → "what needs to happen"
//                 → create the issue (useCreateIssue) → RefineFlow
//        Refine → search an issue → RefineFlow
//
// Everything after the issue exists is RefineFlow (the chat, one step at a
// time). The pre-chat steps here only gather the seed.

import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import type {
  DashboardIssue,
  IntakeProgressPhase,
  IssueEditorPayload,
} from "@loom/shared/dashboard";
import {
  buildLocationAnswer,
  locationAnswerLeaf,
  pathDisplayLabels,
  PATH_SEP,
} from "@loom/shared/code-map";

import { fetchProjects, type Project } from "../projects/useProjects";
import { useCreateIssue } from "../dashboard/useCreateIssue";
import { useIssueDetail } from "../dashboard/useIssueDetail";
import { useProjectIssues } from "../dashboard/useProjectIssues";
import {
  useProjectSession,
  readProjectSessionId,
  clearProjectSessionBinding,
} from "../dashboard/useProjectSession";
import { matchIssues, MAX_ISSUE_RESULTS } from "../dashboard/issueSearch";
import {
  useSessionPagehideBeacon,
  useSessionBridgeEnsure,
  detachSession,
} from "../lib/sessionBeacon";
import { randomId } from "../lib/uuid";
import { isPanelHost, useBootAllowed } from "../lib/host";
import {
  useLocale,
  usePlural,
  useT,
  type MessageKey,
  type Translator,
} from "../i18n/index.js";
import { AccountNavLink } from "../account/AccountNavLink";
import { LapsedConnectionHint } from "../account/LapsedConnectionHint";
import { AdminNavLink } from "../admin/AdminNavLink";
import { ThemeToggle } from "../theme/ThemeToggle";
import { CreateFigmaHint } from "./figma/CreateFigmaHint";
import { MailProposals } from "./mail/MailProposals";
import { MeetingsEntry } from "./meetings/MeetingsEntry";
import { AutoGrowTextarea } from "../ui/AutoGrowTextarea";

import { RefineFlow } from "./RefineFlow";
import { ImplementFlow } from "./ImplementFlow";
import { FocusChat } from "./FocusChat";
import { FocusRailProvider } from "./FocusRailContext";
import { FocusSessionRail } from "./FocusSessionRail";
import { useFocusSessionPick } from "./useFocusSessionPick";
import {
  accessDeniedHelpKey,
  useProjectAccess,
  type AccessDenialReason,
} from "./useProjectAccess";
import { ProjektKartePanel } from "./ProjektKartePanel";
import { useProjectCodeMap } from "./useProjectCodeMap";
import { initialCreateStage } from "./projektKarte";
import {
  BigChoice,
  FocusFrame,
  Headline,
  Help,
  IconChat,
  IconCreate,
  IconImplement,
  IconRefine,
  IconSearch,
  Kicker,
  NextButton,
  OptionButton,
} from "./ui";

interface FocusPageProps {
  /** Chosen project, from the route param. `null` → the project picker. */
  readonly projectId: number | null;
  /** Issue being refined, from the route param. `null` → the wizard. */
  readonly refineIid: number | null;
  /** Project picked → navigate to /focus/projects/$projectId. */
  readonly onPickProject: (projectId: number) => void;
  /** Back out of a project → navigate to /focus. */
  readonly onBackToProjects: () => void;
  /** Enter refine for an issue → navigate to the refine URL (replace). */
  readonly onEnterRefine: (iid: number) => void;
  /** Leave refine → navigate back to /focus/projects/$projectId. */
  readonly onLeaveRefine: () => void;
  /** Open a refined issue in the dashboard detail page. */
  readonly onOpenIssue: (projectId: number, iid: number) => void;
  /**
   * The Focus panel only (`src/panel/`): the issue the assistant's
   * `open_focus` named. The project then opens on the Refine search with that
   * issue first and focused. Never picked for the user: the click on it is
   * what boots the sandbox.
   */
  readonly launchIssueIid?: number;
}

/**
 * A wizard seed: the whole machine-composed turn, plus the byte-exact span
 * inside it that the USER actually authored.
 *
 * A seed is mostly not the user's words — an AI-derived issue title, project-map
 * paths, standing instructions to the agent. The grounding gate quotes user
 * turns, so without a declared span the model's own title comes back to it as
 * certified user evidence. `evidence` is what the gate is allowed to see;
 * `text` is what the agent is told. `evidence` must always be a CONTIGUOUS
 * substring of `text` (the server re-checks and fails closed to "" otherwise),
 * which is why every factory below builds `text` out of `evidence` rather than
 * assembling the two independently.
 */
export interface Seed {
  readonly text: string;
  readonly evidence: string;
}

// Exported ONLY so focus-page.test.tsx can assert the containment invariant over
// every factory and every branch directly. Driving each branch through the
// wizard UI would leave the ones that matter most (an advisory path, an empty
// location) untested, and a span that is not a substring of its own seed fails
// CLOSED server-side to "" — silently reddening every user-grounded slot with a
// fully green suite. That is the failure this export exists to make impossible.
export const makeCreateSeed = (
  iid: number,
  title: string,
  location: string,
  task: string,
  map?: {
    readonly codePaths?: readonly string[];
    readonly advisoryPath?: string;
    /**
     * The SAME confirmed leaf path as `location`, rendered at the viewer's
     * locale (`pathDisplayLabels(...).join(PATH_SEP)`) — i.e. the words the
     * picker actually put on screen. Display-only, and never part of the
     * evidence span: it exists so the seed can tell the model what the area
     * is called in the USER's language, since `location` carries the map's
     * canonical label and is the only name the model would otherwise have.
     */
    readonly locationDisplay?: string;
  },
): Seed => {
  const loc = location.trim();
  // Advisory code pointers from a Projekt-Karte leaf — soft guidance only. The
  // author picked a button on a generated map: the pick or the map itself may
  // be wrong, so the seed must say so — the agent verifies and follows the
  // code, never scopes itself to these.
  const codePaths = (map?.codePaths ?? []).filter((p) => p.trim() !== "");
  const advisory = (map?.advisoryPath ?? "").trim();
  const scopeLine =
    codePaths.length > 0
      ? `\n\nStarting points from the project map (soft guidance only — the ` +
        `author's pick or the map itself may be off; verify against the actual ` +
        `request and follow the code wherever it leads, beyond these paths if ` +
        `needed): ${codePaths.join(", ")}`
      : "";
  // A partially-clicked-then-skipped map path travels as a soft hint only.
  const hintLine =
    !loc && advisory
      ? `\n\nThe author started pointing at "${advisory}" on the project map but ` +
        `wasn't sure — treat it as a hint, not a firm location.`
      : "";
  // The project map is written in the PROJECT's content language (the
  // generator infers it from the README, defaulting to German); the picker
  // shows an English viewer `label_en` instead. `location` is deliberately the
  // canonical label — those are the bytes the grounding gate scans for, so
  // they cannot be localized — but they were also the ONLY name of the area
  // the model ever received, and it wrote them straight into an English
  // session's acceptance criteria ("From Minen verwalten, I can select …",
  // issue #1093). So the display name travels alongside, as machine prose
  // OUTSIDE the evidence span: the stored answer is untouched, only what the
  // model is told changes. Emitted only when the two differ, so a viewer in
  // the map's own language gets exactly the seed they get today.
  const displayLeaf = locationAnswerLeaf(map?.locationDisplay ?? "");
  const canonicalLeaf = locationAnswerLeaf(loc);
  const namingLine =
    loc !== "" && displayLeaf !== "" && displayLeaf !== canonicalLeaf
      ? `\n\nThe project map is written in the project's own language. The area ` +
        `I picked is called "${canonicalLeaf}" there; in my language it is ` +
        `"${displayLeaf}" — use my language's name in anything I will read ` +
        `(acceptance criteria, questions, summaries).`
      : "";
  // The user's own bytes, as ONE contiguous block: the CONFIRMED map location
  // (`Ort: A › B › C` — a visible, self-labelled answer the author picked) on
  // its own line directly above the task they typed.
  //
  // The location used to sit inline in the opening sentence ("a new request
  // about Ort: …"), which put machine prose between the two halves and made the
  // span un-contiguous — the server's containment check would have failed it to
  // "" and RED'd every user-grounded slot on exactly the Projekt-Karte path.
  // Changing the seed text is safe: both `e.text === seed` fences in RefineFlow
  // compare the PERSISTED seed against the echo of that same string.
  //
  // `codePaths` / `advisoryPath` stay OUT — the seed itself calls them not firm,
  // and an advisory path is a click the author explicitly did not confirm.
  const evidence = loc !== "" ? `${loc}\n${task.trim()}` : task.trim();
  const text =
    `I just filed a new request. Here's what's needed:\n\n` +
    `${evidence}${scopeLine}${hintLine}${namingLine}\n\n` +
    `It was created as issue #${iid}: "${title}". Ground in the codebase` +
    `${loc ? ` — the author suggests starting around ${loc}, but follow the code if it points elsewhere` : ""}, then drive #${iid} to fully ready: ` +
    `propose the readiness card, derive what you can from the code and what I ` +
    `told you, ask me only what you genuinely can't determine, and apply once I accept.`;
  return { text, evidence };
};

// Nothing here is the user's: the iid came from a search result and the title is
// GitLab's — frequently Loom's OWN earlier output. So the refine seed declares an
// EMPTY span, and the first card grounds nothing off it. That costs a question
// round on the refine-an-existing-issue flow, deliberately: a title is not a
// decision this session's user made.
export const makeRefineSeed = (iid: number, title: string): Seed => ({
  text:
    `Let's refine issue #${iid}: "${title}". Ground in the codebase first, then ` +
    `propose the readiness card — derive what you can from the code and the issue, ` +
    `and ask me only what you can't determine. Apply once I accept.`,
  evidence: "",
});

/* ─── Reload-survival storage (per-tab) ──────────────────────────────────
 *
 * The refine stage is URL-reflected (/focus/projects/$pid/issues/$iid), but
 * sibling routes REMOUNT on navigation, so the in-memory seed/title don't
 * survive a project→refine navigate or a full reload. We persist the bits a
 * cold refine mount needs:
 *   - the ORIGINAL seed (keyed by pid:iid) so a create-originated refine
 *     byte-matches on reload (a regenerated makeRefineSeed would differ from the
 *     makeCreateSeed the agent first grounded on, defeating RefineFlow's seed
 *     fence and forcing a needless re-ground);
 *   - the create DRAFT (inputs + a stable idempotency key) so a reload mid-create
 *     restores the inputs and a reload-retry dedupes server-side instead of
 *     filing a duplicate issue.
 * Title/webUrl are NOT persisted — re-derived via useIssueDetail on the cold
 * mount (the same fetch IssuePage uses). */

const seedStorageKey = (projectId: number, iid: number): string =>
  `loom.refine.seed:${projectId}:${iid}`;

const persistSeed = (projectId: number, iid: number, seed: Seed): void => {
  try {
    window.sessionStorage.setItem(
      seedStorageKey(projectId, iid),
      JSON.stringify(seed),
    );
  } catch {
    /* best-effort — a regenerated seed still works, it just re-grounds */
  }
};

/** The persisted seed, or null when there is none.
 *
 *  `evidence: undefined` (not `""`) is a deliberate third state: a row written
 *  before the span existed is a bare STRING, and we cannot reconstruct which of
 *  its bytes the user typed. Undefined means "declare no span", so that one tab
 *  reload grounds off the whole seed exactly as it did before the deploy —
 *  fail-open, and only for rows already in a live tab's sessionStorage. */
const readSeed = (
  projectId: number,
  iid: number,
): { readonly text: string; readonly evidence?: string } | null => {
  try {
    const raw = window.sessionStorage.getItem(seedStorageKey(projectId, iid));
    if (raw === null) return null;
    // Legacy plain-string row: not JSON at all, or JSON that isn't our shape.
    // Shape-check both halves — a half-written row must not hand the gate a
    // span that isn't a substring of the text it ships with.
    try {
      const parsed: unknown = JSON.parse(raw);
      if (
        typeof parsed === "object" &&
        parsed !== null &&
        typeof (parsed as Seed).text === "string" &&
        typeof (parsed as Seed).evidence === "string"
      ) {
        return { text: (parsed as Seed).text, evidence: (parsed as Seed).evidence };
      }
    } catch {
      /* fall through to the legacy plain-string reading */
    }
    return { text: raw };
  } catch {
    return null;
  }
};

interface CreateDraft {
  readonly task: string;
  readonly location: string;
  readonly idempotencyKey: string;
  /** Advisory code-path pointers from a Projekt-Karte leaf (optional). */
  readonly codePaths?: readonly string[];
  /** A partially-clicked, then-skipped map path (advisory context, optional).
   *  Canonical bytes — the map's own language — for the seed's grounding span. */
  readonly advisoryPath?: string;
  /** The SAME partial path as `advisoryPath`, at the viewer's own locale — what
   *  the free-text fallback screen shows back to the user (R2-C: the canonical
   *  string is machine-facing only, never UI, per the sibling fix on `location`
   *  below). Optional so a pre-fix persisted draft just shows nothing rather
   *  than the wrong-language string it used to. */
  readonly advisoryPathDisplay?: string;
  /**
   * The confirmed leaf as NODE IDS. `location` holds the canonical answer bytes
   * (`Ort: A › B › C`, what the grounding gate scans for) and those are written in
   * the map's own language — showing them to an English-locale viewer printed
   * German labels for nodes the picker had displayed in English. The ids let both
   * the step-2 echo and the Back-restored panel re-derive labels at the viewer's
   * locale from the same tree the picker rendered.
   */
  readonly mapPath?: readonly string[];
}

const draftStorageKey = (projectId: number): string =>
  `loom.create.draft:${projectId}`;

const readDraft = (projectId: number): CreateDraft | null => {
  try {
    const raw = window.sessionStorage.getItem(draftStorageKey(projectId));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<CreateDraft>;
    if (
      typeof parsed.task !== "string" ||
      typeof parsed.location !== "string" ||
      typeof parsed.idempotencyKey !== "string"
    ) {
      return null;
    }
    return {
      task: parsed.task,
      location: parsed.location,
      idempotencyKey: parsed.idempotencyKey,
      // Optional map fields — old drafts (pre-Projekt-Karte) simply lack them.
      ...(Array.isArray(parsed.codePaths) &&
      parsed.codePaths.every((p) => typeof p === "string")
        ? { codePaths: parsed.codePaths }
        : {}),
      ...(typeof parsed.advisoryPath === "string"
        ? { advisoryPath: parsed.advisoryPath }
        : {}),
      ...(typeof parsed.advisoryPathDisplay === "string"
        ? { advisoryPathDisplay: parsed.advisoryPathDisplay }
        : {}),
      ...(Array.isArray(parsed.mapPath) &&
      parsed.mapPath.every((p) => typeof p === "string")
        ? { mapPath: parsed.mapPath }
        : {}),
    };
  } catch {
    return null;
  }
};

const writeDraft = (projectId: number, draft: CreateDraft): void => {
  try {
    window.sessionStorage.setItem(
      draftStorageKey(projectId),
      JSON.stringify(draft),
    );
  } catch {
    /* best-effort */
  }
};

const clearDraft = (projectId: number): void => {
  try {
    window.sessionStorage.removeItem(draftStorageKey(projectId));
  } catch {
    /* ignore */
  }
};

/* ─── Which MODE the user is in, across a reload (per-tab) ────────────────
 *
 * Refine is URL-reflected; the other modes are component state, so a reload in
 * Chat or Create dropped the user back on the mode picker. (No data was lost —
 * the chat history is server-side and the create inputs are in the draft above —
 * but it reads as one.)
 *
 * A search param would be the other way to hold it, and this is deliberately not
 * that: the marker is cleared on UNMOUNT, which React runs for a real navigation
 * (Back to projects, entering refine) and does NOT run for a reload. So a reload
 * restores the mode and a navigation does not resurrect it — the distinction the
 * URL alone cannot make without also making "Back" mean "back into chat".
 *
 * `implement` is deliberately NOT restorable: ImplementFlow CREATES a dedicated
 * session on mount, so re-entering it from a marker would trigger a second
 * autonomous run against the same issue. `creating` is not either — the POST is
 * in flight or dead, and the draft restores the inputs on the task step. */
type RestorableStageKind =
  | "chat"
  | "search"
  | "implementSearch"
  | "createLocation"
  | "createTask";

const RESTORABLE_STAGES: ReadonlySet<string> = new Set<RestorableStageKind>([
  "chat",
  "search",
  "implementSearch",
  "createLocation",
  "createTask",
]);

const stageStorageKey = (projectId: number): string =>
  `loom.focus.stage:${projectId}`;

const readStage = (projectId: number): RestorableStageKind | null => {
  try {
    const raw = window.sessionStorage.getItem(stageStorageKey(projectId));
    return raw !== null && RESTORABLE_STAGES.has(raw)
      ? (raw as RestorableStageKind)
      : null;
  } catch {
    return null;
  }
};

const writeStage = (projectId: number, kind: string): void => {
  try {
    if (RESTORABLE_STAGES.has(kind)) {
      window.sessionStorage.setItem(stageStorageKey(projectId), kind);
    } else {
      window.sessionStorage.removeItem(stageStorageKey(projectId));
    }
  } catch {
    /* best-effort — a reload then lands on the picker, as it did before */
  }
};

const clearStage = (projectId: number): void => {
  try {
    window.sessionStorage.removeItem(stageStorageKey(projectId));
  } catch {
    /* ignore */
  }
};

/* ─── Fresh-entry marker (deliberately NOT persisted) ────────────────────
 *
 * Every route into the refine chat renders `RefineResume` — the wizard's own
 * create/search navigation as much as a reload — so "RefineResume mounted"
 * cannot by itself tell a fresh flow from a resumed one. This marker does: the
 * wizard records the iid it is navigating into, in MEMORY, and the marker dies
 * with the page. So a mount that finds it was navigated into by this page life
 * (fresh → seed immediately); a mount that does not, was not (resume → seed
 * only once the server proves it no longer holds the flow, see RefineFlow's
 * `resumed` prop). Getting this backwards on a reload is what wiped the agent's
 * conversation mid-refine.
 *
 * Good for exactly ONE mount: a later return to the same refine URL that did not
 * go through the wizard (browser Back, a re-opened link) is a resume, and
 * re-seeding it would destroy the same conversation.
 *
 * Reading and burning it are deliberately SEPARATE calls. `peek` is pure, so
 * every render pass of a mount — including the ones React throws away — agrees
 * on the answer; `consume` runs in the commit phase, so only a mount that really
 * happened spends the marker. Doing both in one render-phase call was wrong in
 * the direction that hurts: StrictMode's discarded pass burned the marker and
 * re-initialised the ref that was meant to hold the answer, so the pass that
 * actually committed read "resume" for a brand-new flow and sat out the resume
 * grace before seeding. */

/* ─── Create-progress copy (phase → catalog key) ─────────────────────────
 *
 * The SSE create stream carries a per-phase `message` written by the workflow
 * that emitted it ("Checking required slots"): developer English, internal
 * vocabulary, never translated. The phase enum is the part that is stable and
 * user-presentable, so it — not the message — drives what the create screen
 * says. */
const CREATE_PROGRESS_KEY: Record<IntakeProgressPhase, MessageKey> = {
  propose_links: "focus.create.progress.linking",
  dor_check: "focus.create.progress.checking",
  creating: "focus.create.progress.writing",
};

const freshRefineEntries = new Set<string>();

const markFreshRefineEntry = (projectId: number, iid: number): void => {
  freshRefineEntries.add(`${projectId}:${iid}`);
};

const peekFreshRefineEntry = (projectId: number, iid: number): boolean =>
  freshRefineEntries.has(`${projectId}:${iid}`);

const consumeFreshRefineEntry = (projectId: number, iid: number): void => {
  freshRefineEntries.delete(`${projectId}:${iid}`);
};

/* ─── Project gate ───────────────────────────────────────────────────── */

function ProjectStep({
  onPick,
}: {
  readonly onPick: (project: Project) => void;
}) {
  const t = useT();
  const [projects, setProjects] = useState<Project[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState("");
  // The empty-query ("recent") result, cached after its first fetch — R2-A:
  // clearing the box used to re-issue `{ recent: true }` every time, and with
  // no loading state of its own the PREVIOUS search's rows sat on screen
  // asserting "these are your projects" for however long that round trip took
  // (measured live: 5–8s). An empty query needs no remote round trip at all —
  // the recent list a fresh mount already fetched is still correct.
  const recentRef = useRef<Project[] | null>(null);

  useEffect(() => {
    let cancelled = false;
    const q = query.trim();
    if (q === "") {
      if (recentRef.current !== null) {
        setProjects(recentRef.current);
        setError(null);
        return;
      }
      // `includeGithub`: the user's GitHub repositories join the list when
      // GitHub is connected; the server leaves them out otherwise.
      fetchProjects({ recent: true, includeGithub: true })
        .then((next) => {
          if (cancelled) return;
          recentRef.current = next;
          setProjects(next);
          setError(null);
        })
        .catch((err: Error) => {
          if (!cancelled) setError(err.message);
        });
      return () => {
        cancelled = true;
      };
    }
    // Server-side narrowing: typing debounces a `search` round-trip — the walk
    // and matching happen in GitLab, not over a fully-buffered client list.
    // Previous results stay visible during a refetch (no loading flash).
    const handle = setTimeout(() => {
      fetchProjects({ search: q, includeGithub: true })
        .then((next) => {
          if (cancelled) return;
          setProjects(next);
          setError(null);
        })
        .catch((err: Error) => {
          if (!cancelled) setError(err.message);
        });
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(handle);
    };
  }, [query]);

  const results = (projects ?? []).slice(0, 8);

  return (
    <FocusFrame
      stepKey="project"
      progress={0.02}
      heroLogo
      // Account chrome, like the main menu's: the account link and the
      // appearance control (System / Light / Dark) ride the top bar, never a
      // flow step. No "Meetings" here: meetings are a project's, so their
      // button waits for the project's selection screen.
      chromeAside={
        <>
          <AccountNavLink />
          <ThemeToggle />
        </>
      }
    >
      <Kicker>{t("focus.project.kicker")}</Kicker>
      <Headline>{t("focus.project.headline")}</Headline>
      <input
        className="focus-field"
        autoFocus
        aria-label={t("focus.project.searchAria")}
        placeholder={t("focus.project.searchPlaceholder")}
        value={query}
        onChange={(e) => setQuery(e.target.value)}
      />
      {projects === null && !error && <Help>{t("focus.project.loading")}</Help>}
      {error && (
        <div className="focus-error">{t("focus.project.loadError", { error })}</div>
      )}
      {projects !== null && (
        <div className="focus-options">
          {results.length === 0 ? (
            <Help>
              {query.trim() === ""
                ? t("focus.project.noneFound")
                : t("focus.project.noMatch", { query })}
            </Help>
          ) : (
            results.map((p) => (
              <OptionButton
                key={p.id}
                label={p.name_with_namespace}
                onClick={() => onPick(p)}
              />
            ))
          )}
        </div>
      )}
      {/* Only while a lapsed GitHub connection keeps projects out of the
          list; nothing otherwise, including where GitHub is off. */}
      <LapsedConnectionHint />
    </FocusFrame>
  );
}

/* ─── Issue search (refine) ──────────────────────────────────────────── */

/**
 * The quiet second line under an issue in the pickers: its number, its stage,
 * and — when they exist — the two facts a refiner is actually choosing between
 * (is it already ready, how big is it).
 *
 * Composed from the projected `DashboardIssue`, never from the raw `labels`
 * array: those are scoped label ids (`loom::dor:pass`, `points::3`) and printing
 * them here would put Loom's internal vocabulary in front of a business owner.
 */
const issueMeta = (
  t: Translator,
  tn: ReturnType<typeof usePlural>,
  issue: DashboardIssue,
): string =>
  [
    `#${issue.iid}`,
    t(`dashboard.issueStage.${issue.stage}` as MessageKey),
    issue.dor === "pass" ? t("focus.search.meta.ready") : null,
    // `n=1` is a real value on `STORY_POINT_SCALE` — plural-aware so it reads
    // "1 point"/"1 Punkt" rather than the ungrammatical "1 points"/"1 Punkte".
    issue.points !== null ? tn("plural.points", issue.points) : null,
    issue.milestone ? issue.milestone.title : null,
  ]
    .filter((p): p is string => p !== null)
    .join(" · ");

function SearchStep({
  projectId,
  onPick,
  onBack,
  variant = "refine",
  preselectIid,
}: {
  readonly projectId: number;
  readonly onPick: (issue: DashboardIssue) => void;
  readonly onBack: () => void;
  /** Copy variant — the picker is shared by Refine and Implement. */
  readonly variant?: "refine" | "implement";
  /**
   * The issue the panel's launch named: listed first (while the query is
   * empty) and focused instead of the search field. Never picked.
   */
  readonly preselectIid?: number;
}) {
  const t = useT();
  const tn = usePlural();
  const { issues, loading, error } = useProjectIssues(projectId, { openOnly: true });
  const [query, setQuery] = useState("");
  // An EMPTY query lists the project's most recent open issues rather than
  // nothing. `matchIssues` returns [] for "" (correct for the dashboard's
  // type-ahead combobox, which must not drop a list over the page), but here the
  // list IS the step: a picker that shows nothing until you type demands you
  // already know the title of the issue you are looking for.
  const results = useMemo(() => {
    if (query.trim() !== "") return matchIssues(issues, query);
    const launched =
      preselectIid === undefined ? undefined : issues.find((i) => i.iid === preselectIid);
    return launched === undefined
      ? issues.slice(0, MAX_ISSUE_RESULTS)
      : [launched, ...issues.filter((i) => i !== launched)].slice(0, MAX_ISSUE_RESULTS);
  }, [issues, query, preselectIid]);
  const preselected =
    preselectIid !== undefined && query.trim() === "" && results[0]?.iid === preselectIid;
  const kicker =
    variant === "implement"
      ? t("focus.search.implementKicker")
      : t("focus.search.refineKicker");
  const headline =
    variant === "implement"
      ? t("focus.search.implementHeadline")
      : t("focus.search.refineHeadline");

  return (
    <FocusFrame
      stepKey={variant === "implement" ? "implementSearch" : "search"}
      progress={0.16}
      onBack={onBack}
    >
      <Kicker>
        <IconSearch /> {kicker}
      </Kicker>
      <Headline>{headline}</Headline>
      <Help>{t("focus.search.help")}</Help>
      <input
        className="focus-field"
        // The launched issue takes the focus once it is listed; until then
        // (and without one) the field has it, as always.
        autoFocus={!preselected}
        aria-label={t("focus.search.aria")}
        placeholder={t("focus.search.placeholder")}
        value={query}
        onChange={(e) => setQuery(e.target.value)}
      />
      {loading && <Help>{t("focus.search.loading")}</Help>}
      {error && <div className="focus-error">{error}</div>}
      {/* `!error` too: a failed fetch also leaves the list empty, and claiming
          the project has no open requests would be a second, wrong explanation
          stacked under the real one. */}
      {!loading && !error && (
        <div className="focus-options">
          {results.length === 0 ? (
            <Help>
              {query.trim() === ""
                ? t("focus.search.empty")
                : t("focus.search.noMatch")}
            </Help>
          ) : (
            results.map((i) => (
              <OptionButton
                key={i.iid}
                label={i.title}
                // The identity and the state of the issue, which a bare title
                // does not carry: two issues in a project routinely read alike,
                // and "#1081 · Ready · 3 points" is what tells them apart.
                sub={issueMeta(t, tn, i)}
                onClick={() => onPick(i)}
                {...(preselected && i.iid === preselectIid ? { autoFocus: true } : {})}
              />
            ))
          )}
        </div>
      )}
    </FocusFrame>
  );
}

/* ─── Per-project wizard ─────────────────────────────────────────────── */

// Ephemeral wizard steps held in component state. The REFINE stage is NOT here:
// it is URL-reflected (/focus/projects/$pid/issues/$iid) and rendered by
// `RefineResume`, so it survives reload. Only the transient gather-the-seed
// steps stay internal.
type Stage =
  | { readonly kind: "intro" }
  | { readonly kind: "createLocation" }
  | { readonly kind: "createTask" }
  | { readonly kind: "creating" }
  | { readonly kind: "search" }
  | { readonly kind: "implementSearch" }
  | {
      readonly kind: "implement";
      readonly iid: number;
      readonly title: string;
      readonly webUrl: string | null;
      /**
       * RESUME an existing autonomous run rather than opening a new one. Set
       * only by a history pick: an implement run owns a DEDICATED session, so
       * it is reopened by id and the per-project PM binding is left alone.
       */
      readonly resumeSessionId?: string;
    }
  | { readonly kind: "chat" };

/**
 * The stages whose next step actually uses a sandbox. The pre-warm fires on
 * these and nowhere else.
 *
 * `intro` is four buttons and no data entry, and every path out of it passes
 * through a stage in this set, so warming there buys nothing and costs a
 * GitLab pipeline for a session no surface on that stage attaches to — billed
 * for its whole idle window, and stamped `unknown` at create, so it is not even
 * a row anyone can find again. `implementSearch` and `implement` are excluded
 * too — an autonomous run owns a DEDICATED session and mints its own.
 *
 * The SLOT is chosen by the stage: `chat` warms the project's chat binding,
 * every other stage in the set warms the issue slot's PRE-WARM, which the first
 * issue picked or created claims. Warming a stage therefore never touches the
 * other slot's session.
 *
 * This set gates the bridge RE-ARM below as well, and it has to: the two decide
 * the same thing (does this stage need a live sandbox), and a re-arm on a stage
 * outside the set boots a pipeline the pre-warm deliberately declined to.
 *
 * What the gate buys is "a landing with NO prior binding mints nothing". It is
 * not "no landing ever costs a pipeline": `useProjectSession` seeds `sessionId`
 * from what the tab already holds in its useState initialiser (the chat's
 * sessionStorage mirror, the issue slot's in-memory pre-warm), so a tab that
 * holds one re-arms the ACTIVE slot through `useSessionBridgeEnsure` on the
 * stages in this set. That re-arm IS the contract — it is what recovers a dead
 * sandbox — and it stays.
 */
const SANDBOX_STAGES: ReadonlySet<Stage["kind"]> = new Set([
  "createLocation",
  "createTask",
  "creating",
  "search",
  "chat",
]);

/* ─── Stage handoff across a route swap ──────────────────────────────────
 *
 * Where a history pick's landing stage lives between the click and the mount
 * that renders it.
 *
 * It cannot be component state on the surface that runs the pick: the three
 * Focus routes are SIBLINGS with three different component functions, so
 * leaving the refine URL swaps the rendered component and React unmounts the
 * whole subtree — `FocusView` → `FocusPage` → `ProjectSurface` — before the new
 * one mounts. A stage set on the outgoing instance dies with it, and the user
 * lands on the mode picker with the picked session silently bound underneath.
 *
 * Module scope survives that swap (it is one client-side navigation, not a
 * reload) and READ-ONCE is the whole safety property: the claim is consumed by
 * the next `ProjectFocus` state initializer and gone, so a stage picked minutes
 * ago can never re-apply on a later mount. `sessionStorage` is deliberately not
 * used — `implement` carries an issue identity plus a `resumeSessionId` that
 * `RESTORABLE_STAGES` refuses on purpose (a restored implement stage would open
 * a second autonomous run), and a claim must not survive a reload. */
const stageHandoff = new Map<number, Stage>();

const claimStageHandoff = (projectId: number, stage: Stage): void => {
  stageHandoff.set(projectId, stage);
};

const consumeStageHandoff = (projectId: number): Stage | null => {
  const stage = stageHandoff.get(projectId);
  if (stage === undefined) return null;
  stageHandoff.delete(projectId);
  return stage;
};

function ProjectFocus({
  projectId,
  onBackToProjects,
  onOpenIssue,
  onEnterRefine,
  onChatStageChange,
  launchIssueIid,
}: {
  readonly projectId: number;
  /** The panel's launch issue (`FocusPageProps.launchIssueIid`). */
  readonly launchIssueIid?: number;
  readonly onBackToProjects: () => void;
  readonly onOpenIssue: (iid: number) => void;
  /** Navigate into the URL-reflected refine stage for the given issue. */
  readonly onEnterRefine: (iid: number) => void;
  /**
   * Report whether the current stage IS the chat, i.e. which of the project's
   * two bindings this surface stands in. The stage lives here and the rail and
   * the pick machine live above, and neither can read it off the URL — every
   * wizard stage shares the project URL with the chat.
   */
  readonly onChatStageChange: (isChat: boolean) => void;
}) {
  const t = useT();
  const { locale } = useLocale();
  // Restore a create draft saved by a prior mount (reload mid-create). When a
  // task survived, land back on the task step rather than the intro.
  const restoredDraft = useMemo(() => readDraft(projectId), [projectId]);
  // The mode this tab was in before a reload wins; the draft rule is the
  // fallback (a task survived → land on the task step, not the picker).
  const restoredStage = useMemo(() => readStage(projectId), [projectId]);
  // A history pick's stage outranks both the reload-restored mode and the draft
  // rule — the user just chose something else. Consumed here and only here: the
  // `writeStage` effect below rewrites the sessionStorage key from React state
  // on the next commit and the unmount `clearStage` runs before a keyed remount
  // could read it, so the stage cannot be handed over through that key.
  // The panel's launch issue (an assistant named it) lands on the Refine
  // search after a restored mode and before the draft rule: a re-mount of the
  // same panel shows what the user left, not the launch again.
  const [stage, setStage] = useState<Stage>(() => {
    const handed = consumeStageHandoff(projectId);
    return handed !== null
      ? handed
      : restoredStage !== null
        ? { kind: restoredStage }
        : launchIssueIid !== undefined
          ? { kind: "search" }
          : restoredDraft && restoredDraft.task.trim() !== ""
            ? { kind: "createTask" }
            : { kind: "intro" };
  });
  const [location, setLocation] = useState(restoredDraft?.location ?? "");
  const [task, setTask] = useState(restoredDraft?.task ?? "");
  // Projekt-Karte state. The map is fetched on mount; the create flow renders
  // the button panel (map) or skips the location step entirely (no map), and
  // falls back to the free-text question on skip/error. `codePaths` are the
  // advisory pointers from a chosen leaf; `advisoryPath` is a partial path the
  // user skipped mid-drill (both ride into the create seed, not the issue).
  const mapState = useProjectCodeMap(projectId);
  const [codePaths, setCodePaths] = useState<readonly string[]>(
    restoredDraft?.codePaths ?? [],
  );
  const [advisoryPath, setAdvisoryPath] = useState(
    restoredDraft?.advisoryPath ?? "",
  );
  // Display twin of `advisoryPath` (R2-C) — same partial path, the viewer's own
  // locale, UI-facing only. See the field's own doc on `CreateDraft`.
  const [advisoryPathDisplay, setAdvisoryPathDisplay] = useState(
    restoredDraft?.advisoryPathDisplay ?? "",
  );
  // The confirmed leaf as node ids — see `CreateDraft.mapPath`.
  const [mapPath, setMapPath] = useState<readonly string[]>(
    restoredDraft?.mapPath ?? [],
  );
  // Whether the user tapped the panel's escape hatch, switching a map project to
  // the free-text fallback for this attempt.
  const [mapSkipped, setMapSkipped] = useState(false);
  // What step 2 shows as "the location you picked". Map picks render from the
  // node ids at the VIEWER's locale — `location` itself is the canonical answer
  // string in the map's own language and must never be shown as UI. A typed
  // location is the user's own text and is shown verbatim.
  const chosenLocationLabel =
    mapPath.length > 0 && mapState.status === "map"
      ? pathDisplayLabels(mapState.tree, [...mapPath], locale).join(PATH_SEP)
      : location.trim() !== "" && mapPath.length === 0
        ? location.trim()
        : null;
  // Stable per-draft idempotency key: minted once, reused across a reload-retry
  // (restored from the draft) so the server dedupes a re-submit. Re-armed after
  // a successful create.
  const idempotencyKeyRef = useRef(restoredDraft?.idempotencyKey ?? randomId());
  const [createError, setCreateError] = useState<string | null>(null);
  const create = useCreateIssue(projectId);

  // This component owns BOTH of the project's unclaimed bindings: the issue
  // slot's pre-warm (no issue named — the refine surface of the issue picked or
  // created claims it, `useProjectSession`) and the chat's own. That is what
  // lets the beacon cover both while only the stage's own slot is armed.
  const issueSession = useProjectSession(projectId, "issue");
  const chatSession = useProjectSession(projectId, "chat");
  const activeSlotSession = stage.kind === "chat" ? chatSession : issueSession;

  // Remount key for FocusChat. `FocusChat` holds a SECOND
  // `useProjectSession(projectId, "chat")` — two independent React states over
  // one module cache with no subscription between them — so a reset must be
  // followed by a remount or the chat window keeps rendering the old id.
  const [chatEpoch, setChatEpoch] = useState(0);

  // "Start a new chat", owned HERE rather than in `FocusChat`, because this
  // component is the one holding the binding the pagehide beacon reads: a
  // `reset()` inside FocusChat would leave `chatSession.sessionId` pinned to the
  // dead id, so the beacon would detach the corpse while the NEW sandbox got no
  // pagehide detach at all and aged out on the 60-minute idle horizon instead of
  // the 90s grace.
  const { reset: resetChatSession, ensure: ensureChatSession } = chatSession;
  const startFreshChat = useCallback(() => {
    // The MODULE CACHE is the authority, not this component's state: a rail pick
    // calls `adoptProjectSession`, which moves the binding without touching
    // either hook's state.
    const outgoing = readProjectSessionId(projectId, "chat");
    // `?reason=pagehide` and NEVER the bare DELETE — the bare form stamps the
    // write-once `endedAt` latch that nothing clears. This hands the outgoing
    // sandbox to the 90s detach grace, so it stays listed and reopenable.
    if (outgoing !== null) detachSession(outgoing);
    // `reset()`, not `clearPersistedSession`: the module cache is read first, so
    // a storage-only clear is invisible until a reload.
    resetChatSession();
    // Minted from THIS instance, so `chatSession.sessionId` — and therefore the
    // beacon below — follows the new session. FocusChat's own mount-effect
    // `ensure()` joins the in-flight promise this starts.
    ensureChatSession();
    setChatEpoch((n) => n + 1);
  }, [projectId, resetChatSession, ensureChatSession]);

  // Pre-warm the sandbox behind the ACTIVE slot the moment the user enters a
  // stage that will need one (`SANDBOX_STAGES` above) — still well before they
  // finish typing the seed. Booting now overlaps the cold-start with that human
  // time: by the time RefineFlow mounts the bridge is warmer — often already
  // `ready`, so the seed grounds in the real code immediately instead of
  // cold-booting.
  // `intro` is outside the set: a landing that only browses the history rail
  // must not mint a session, and every such mint is a billed sandbox nothing
  // attaches to.
  // `ensure` is idempotent (module-scope cache + shared in-flight promise in
  // useProjectSession), so RefineFlow's own mount-time `ensure()` CLAIMS this
  // session for its issue (or joins the mint still in flight) rather than
  // triggering a second `POST /sessions` / pipeline. Claimed, it is gone from
  // here: the next visit to this stage warms a new one, so no two issues share
  // a session.
  // `error` is deliberately NOT read here: a transient boot failure belongs to
  // the one flow that hit it, each of which surfaces and retries its own.
  const {
    ensure: prewarmSession,
    sessionId,
    accessDenied: sessionAccessDenied,
    starting: sessionStarting,
  } = activeSlotSession;
  // In the Focus panel the pre-warm waits for the first gesture inside it
  // (`lib/host.ts`, the boot gate): a panel nobody has touched boots nothing,
  // and the stage it warms is the one on screen when the gate opens. Always
  // open on the web.
  const bootAllowed = useBootAllowed();
  useEffect(() => {
    if (!bootAllowed) return;
    if (!SANDBOX_STAGES.has(stage.kind)) return;
    prewarmSession();
  }, [prewarmSession, stage.kind, bootAllowed]);
  // Publish the slot this surface stands in, so the drawer above renders "open
  // now" against the binding the user is actually in and its pick guard
  // swallows only that row.
  useEffect(() => {
    onChatStageChange(stage.kind === "chat");
  }, [onChatStageChange, stage.kind]);
  // Whether this (user, project) may work here at all, asked as a content-free
  // probe rather than inferred from a failed session mint. It costs no pipeline,
  // which is what lets the refusal below stand on `intro`, where the pre-warm
  // deliberately does not fire.
  const access = useProjectAccess(projectId);

  // Tab close / reload → DETACH the project sandbox (kept alive for the server's
  // reconnect grace so a reload re-adopts it, rather than cold-cancelling).
  // BOTH slots: a tab that chatted and then refined holds two live sandboxes,
  // and the one the user is not standing in is exactly the one that would be
  // orphaned to a hard reap rather than entering the grace.
  // `detachOnUnmount` rides ONLY the chat slot, and only while the chat stage
  // is the one on screen — that is the single stage here whose surface
  // (`FocusChat`) opens a WebSocket, and therefore the only one whose return
  // can clear the detach mark (`markUsed` has one caller: the WS attach).
  // The issue slot is never armed from here: the surface that holds its socket
  // is `RefineFlow`, on a sibling ROUTE, which beacons it itself. Arming the
  // wizard/picker stages instead reaped their pre-warmed sandbox ~150s after
  // any in-app navigation, with the tab still open in front of it.
  useSessionPagehideBeacon(issueSession.sessionId);
  useSessionPagehideBeacon(chatSession.sessionId, {
    detachOnUnmount: stage.kind === "chat",
  });

  // The prewarm above only warms something if the bound session still HAS a
  // sandbox — one bound earlier (the chat a reload re-adopted, or a pre-warm
  // this tab minted whose sandbox has since died) skipped `POST /sessions`, so
  // there is no pipeline to warm. Re-arm the bridge so the wizard's whole point
  // (a warm sandbox by the time refine opens) holds. The issue slot's pre-warm
  // itself does not survive a reload: it is never persisted, so a reload mints
  // a new one (`useProjectSession`).
  //
  // Gated on the SAME `SANDBOX_STAGES` set the pre-warm is, and on the active
  // slot within it — the two must agree. A stage outside the set arms nothing:
  // `intro` / `implementSearch` / `implement` attach no surface to the project
  // binding, so re-arming there boots a pipeline for a session nobody will use,
  // once per return to the stage. The slot the user is not looking at is left
  // alone for the same reason; its sandbox ages out on the server's idle
  // timeout once its WS closes, which is also why a stage switch detaches
  // nothing — an eager detach would cold-boot on every Chat ↔ Refine toggle.
  // ONE exception, and it lives in `FocusChat`: the chat surface detaches its
  // own binding on unmount when it was never spoken to, where there is no
  // conversation to cold-boot back into.
  useSessionBridgeEnsure(SANDBOX_STAGES.has(stage.kind) ? sessionId : null);

  // Persist the create draft (inputs + the SAME idempotency key) so a reload
  // mid-create restores them. Cleared when both inputs are empty.
  useEffect(() => {
    if (
      task.trim() === "" &&
      location.trim() === "" &&
      codePaths.length === 0 &&
      advisoryPath === "" &&
      mapPath.length === 0
    ) {
      clearDraft(projectId);
      return;
    }
    writeDraft(projectId, {
      task,
      location,
      idempotencyKey: idempotencyKeyRef.current,
      ...(codePaths.length > 0 ? { codePaths } : {}),
      ...(advisoryPath !== "" ? { advisoryPath } : {}),
      ...(advisoryPathDisplay !== "" ? { advisoryPathDisplay } : {}),
      ...(mapPath.length > 0 ? { mapPath } : {}),
    });
  }, [
    projectId,
    task,
    location,
    codePaths,
    advisoryPath,
    advisoryPathDisplay,
    mapPath,
  ]);

  // Hold the MODE across a reload, and only across a reload — see the
  // `readStage` block. The cleanup runs on a real unmount (navigating back to the
  // project picker, or into the refine URL), where re-entry must show the picker.
  useEffect(() => {
    writeStage(projectId, stage.kind);
  }, [projectId, stage.kind]);
  useEffect(() => () => clearStage(projectId), [projectId]);

  // No-map projects skip the location step entirely: if the map resolves to
  // "none" while we're on it (e.g. the user clicked Create before the fetch
  // landed), advance to the task step. The task step's Back target drops
  // location too, so this never loops.
  useEffect(() => {
    if (stage.kind === "createLocation" && mapState.status === "none") {
      setStage({ kind: "createTask" });
    }
  }, [stage.kind, mapState.status]);

  // Persist the seed for this issue, then navigate into the refine URL. The seed
  // is stored (not passed) because the refine route remounts fresh.
  const enterRefine = (iid: number, seed: Seed): void => {
    persistSeed(projectId, iid, seed);
    // This navigation IS the flow opening — tell the refine mount it may seed
    // at once instead of waiting to see whether the server already has a flow
    // (it cannot: the issue was just created or picked).
    markFreshRefineEntry(projectId, iid);
    onEnterRefine(iid);
  };

  const runCreate = async (): Promise<void> => {
    if (task.trim() === "") return;
    setCreateError(null);
    setStage({ kind: "creating" });
    try {
      // Empty — the server derives it (AI, falling back to a deterministic
      // heuristic) in the shared apply seam. See `resolveTitle` in
      // apps/server/src/dashboard/apply.ts.
      const payload: IssueEditorPayload = {
        title: "",
        description: task.trim(),
        outcome: "",
        acceptanceCriteria: [],
        priority: "normal",
      };
      const result = await create.submit(
        payload,
        [],
        idempotencyKeyRef.current,
      );
      const iid = result.issue.iid;
      // `chosenLocationLabel` is exactly what step 2 showed as "the location
      // you picked" — the confirmed leaf path at the VIEWER's locale — so
      // passing it is what lets the seed name the area in the user's own
      // language without touching the canonical `location` bytes. Null (a
      // typed location, or no map pick) leaves the naming line off.
      const seed = makeCreateSeed(iid, result.issue.title, location, task, {
        codePaths,
        advisoryPath,
        ...(mapPath.length > 0 && chosenLocationLabel !== null
          ? { locationDisplay: chosenLocationLabel }
          : {}),
      });
      // The draft did its job; clear it and re-arm a fresh key for the next one.
      clearDraft(projectId);
      idempotencyKeyRef.current = randomId();
      enterRefine(iid, seed);
    } catch (err) {
      setCreateError(err instanceof Error ? err.message : String(err));
      setStage({ kind: "createTask" });
    }
  };

  // Projekt-Karte leaf confirmed → record the visible self-labelled answer
  // (`Ort: A › B › C`, quotable by the grounding gate) + the advisory code
  // paths, then advance to the task step.
  const onMapLeaf = (
    labelPath: string[],
    leafCodePaths: string[],
    nodePath: string[],
  ): void => {
    setLocation(buildLocationAnswer(labelPath));
    setCodePaths(leafCodePaths);
    setAdvisoryPath("");
    setAdvisoryPathDisplay("");
    setMapPath(nodePath);
    setStage({ kind: "createTask" });
  };
  // Escape hatch → fall back to the free-text question; a partially-clicked
  // path travels along as advisory context.
  //
  // A map-derived `location` is DROPPED here. It holds the canonical answer
  // bytes for a leaf the user has just walked away from, and leaving them in
  // state pre-filled the free-text box with `Ort: Rematerial-Shop › Bestellen &
  // bezahlen` — machine-composed, in the map's own language, for a node the
  // picker had shown in English. Free text the user typed themselves is left
  // alone: `mapPath` is non-empty only for a confirmed leaf.
  //
  // `partialDisplayPath` is the sibling of that same fix (R2-C): the panel
  // hands back BOTH the canonical path (what the seed's grounding span needs —
  // see `hintLine` in `makeCreateSeed`) and the display path (what this screen
  // shows back). A single string here can't serve both — the canonical one is
  // the map's own language, and showing it printed German node labels under an
  // English-locale escape-hatch box for nodes the picker had just rendered in
  // English.
  const onMapSkip = (
    partialLabelPath: string[],
    partialDisplayPath: string[],
  ): void => {
    setMapSkipped(true);
    setAdvisoryPath(partialLabelPath.join(PATH_SEP));
    setAdvisoryPathDisplay(partialDisplayPath.join(PATH_SEP));
    if (mapPath.length > 0) {
      setLocation("");
      setCodePaths([]);
      setMapPath([]);
    }
  };

  // Every mode below (chat, refine, implement, and create's eventual
  // RefineFlow) needs a project the user may actually work in. An inaccessible
  // project rendering exactly like an accessible one is the F18 bug: whichever
  // mode was picked then sat on "waking up" forever with no explanation and no
  // way out. So the refusal comes first, from two sources.
  //
  // The PROBE is the primary one — a content-free `GET /api/projects/:id/access`
  // that costs no session and no pipeline, and answers before any mode is
  // chosen. `sessionAccessDenied` still stands beside it: a mint that came back
  // 403 has proven the same thing the harder way.
  //
  // Both are ACCESS answers, a property of (user, project) that every mode
  // hits alike, including Implement with its own dedicated session — so
  // blocking the picker tells the truth. Neither a TRANSIENT session failure
  // (503, a one-off 500) nor a probe that could not answer denies anything:
  // each flow surfaces its own boot failure with its own retry, and turning a
  // blip into a locked project is the over-reach to avoid.
  // The reason comes from the probe when the probe answered. A 403 on the mint
  // names no leg — GitLab pre-flight and Loom's own gate both reply 403 there —
  // so it resolves to `unknown`, whose line states the consequence without
  // claiming a cause it cannot prove.
  const denialReason: AccessDenialReason | null =
    access.state.kind === "ready" && access.state.denial !== null
      ? access.state.denial
      : sessionAccessDenied === true
        ? "unknown"
        : null;
  if (denialReason !== null) {
    return (
      <FocusFrame
        stepKey="intro"
        progress={0.08}
        onBack={onBackToProjects}
        heroLogo
      >
        <Kicker>{t("common.loom")}</Kicker>
        <Headline>{t("focus.status.accessDenied")}</Headline>
        {/* A 403 is not retryable by the person reading it, so the help names
            who can change it rather than echoing the server's line — and names
            the RIGHT one: a GitLab role and a Loom grant are different errands.
            Retry still stands: they can come back once someone grants it. */}
        <div className="focus-error">{t(accessDeniedHelpKey(denialReason))}</div>
        <div className="focus-footer">
          <button
            type="button"
            className="focus-btn"
            onClick={() => {
              access.retry();
              prewarmSession();
            }}
            disabled={sessionStarting}
          >
            {sessionStarting
              ? t("focus.refine.bootFailed.retrying")
              : t("focus.refine.bootFailed.retry")}
          </button>
        </div>
      </FocusFrame>
    );
  }

  if (stage.kind === "search") {
    return (
      <SearchStep
        projectId={projectId}
        {...(launchIssueIid !== undefined ? { preselectIid: launchIssueIid } : {})}
        onBack={() => setStage({ kind: "intro" })}
        onPick={(issue) =>
          enterRefine(issue.iid, makeRefineSeed(issue.iid, issue.title))
        }
      />
    );
  }

  if (stage.kind === "implementSearch") {
    return (
      <SearchStep
        projectId={projectId}
        variant="implement"
        onBack={() => setStage({ kind: "intro" })}
        onPick={(issue) =>
          setStage({
            kind: "implement",
            iid: issue.iid,
            title: issue.title,
            webUrl: issue.webUrl,
          })
        }
      />
    );
  }

  if (stage.kind === "implement") {
    return (
      <ImplementFlow
        key={stage.iid}
        projectId={projectId}
        iid={stage.iid}
        // Set only on a resumed run; ImplementFlow then attaches to that
        // session instead of creating one.
        {...(stage.resumeSessionId !== undefined
          ? { resumeSessionId: stage.resumeSessionId }
          : {})}
        issueTitle={stage.title}
        issueWebUrl={stage.webUrl}
        onRestart={() => setStage({ kind: "intro" })}
        // The halt screen's Back — "pick a different issue instead" is its
        // own primary recovery path, so send it to the issue picker rather
        // than two Backs out at the mode picker.
        onPickAnotherIssue={() => setStage({ kind: "implementSearch" })}
        onOpenIssue={onOpenIssue}
        onRefineInstead={() =>
          enterRefine(stage.iid, makeRefineSeed(stage.iid, stage.title))
        }
        // Re-target the flow at the sub-issue: same surface, new implement
        // session (ImplementFlow is keyed by iid, so it remounts and opens one).
        // The title/URL are unknown here — the epic frame only carries child
        // iids — ImplementFlow fetches each child's own title client-side
        // (`EpicChildChip`) for its chip label.
        onImplementChild={(childIid) =>
          setStage({ kind: "implement", iid: childIid, title: "", webUrl: null })
        }
      />
    );
  }

  if (stage.kind === "chat") {
    return (
      <FocusChat
        key={chatEpoch}
        projectId={projectId}
        onBack={() => setStage({ kind: "intro" })}
        onNewChat={startFreshChat}
      />
    );
  }

  if (stage.kind === "intro") {
    return (
      <FocusFrame
        stepKey="intro"
        progress={0.08}
        onBack={onBackToProjects}
        heroLogo
        // Account chrome, so it rides the top bar: the account link
        // (connections, sign-out) and the appearance control (System / Light /
        // Dark).
        chromeAside={
          <>
            <AccountNavLink />
            <ThemeToggle />
          </>
        }
      >
        <Kicker>{t("common.loom")}</Kicker>
        <Headline>{t("focus.intro.headline")}</Headline>
        <div className="focus-choices">
          <BigChoice
            icon={<IconCreate />}
            title={t("focus.intro.create.title")}
            onClick={() =>
              setStage({ kind: initialCreateStage(mapState) })
            }
          />
          <BigChoice
            icon={<IconRefine />}
            title={t("focus.intro.refine.title")}
            onClick={() => setStage({ kind: "search" })}
          />
          <BigChoice
            icon={<IconImplement />}
            title={t("focus.intro.implement.title")}
            onClick={() => setStage({ kind: "implementSearch" })}
          />
          <BigChoice
            icon={<IconChat />}
            title={t("focus.intro.chat.title")}
            onClick={() => setStage({ kind: "chat" })}
          />
        </div>
        {/* Meetings, mail proposals and the admin link stay in Loom's web
            app: the Focus panel (an assistant's MCP App) leaves all three
            out. */}
        {!isPanelHost() && (
          <>
            {/* This project's meetings: only with the feature on and Google
                connected (on the account page). The one way into /focus/meetings. */}
            <MeetingsEntry projectId={projectId} />
            <MailProposals projectId={projectId} />
            {/* Admin-only entry to /admin/access. Renders null for non-admins; the
                route + API are server-gated regardless. There's no global nav
                chrome, so it lives on this visited authenticated surface. */}
            <div className="focus-admin-link">
              <AdminNavLink />
            </div>
          </>
        )}
      </FocusFrame>
    );
  }

  if (stage.kind === "createLocation") {
    return (
      <FocusFrame
        stepKey="createLocation"
        progress={0.16}
        onBack={() => setStage({ kind: "intro" })}
      >
        <Kicker num={t("focus.create.location.num")}>{t("focus.create.kicker")}</Kicker>
        <Headline>{t("focus.create.location.headline")}</Headline>
        {mapState.status === "map" && !mapSkipped ? (
          // Project HAS a map → the clickable Projekt-Karte replaces the
          // free-text question. A leaf lands a self-labelled quotable answer;
          // the skip escape hatch falls back to the free-text view below.
          <ProjektKartePanel
            tree={mapState.tree}
            onLeaf={onMapLeaf}
            onSkip={onMapSkip}
            // Re-open on the confirmed leaf when the user came Back from step 2
            // (or reloaded): the panel used to reset to the root, so their own
            // answer vanished off the screen.
            initialPath={mapPath}
          />
        ) : mapState.status === "loading" || mapState.status === "none" ? (
          // Fetch in flight, or a no-map project mid-advance to the task step.
          <p className="pk-loading">
            {t("focus.create.location.map.loading")}
          </p>
        ) : (
          // Free-text fallback: no map (error) or the user skipped the panel.
          <>
            <Help>{t("focus.create.location.help")}</Help>
            {advisoryPathDisplay !== "" && (
              <p className="pk-advisory">
                {t("focus.create.location.map.advisory", {
                  path: advisoryPathDisplay,
                })}
              </p>
            )}
            <AutoGrowTextarea
              className="focus-field is-box"
              autoFocus
              minLines={3}
              aria-label={t("focus.create.location.aria")}
              placeholder={t("focus.create.location.placeholder")}
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
                  e.preventDefault();
                  setStage({ kind: "createTask" });
                }
              }}
            />
            <div className="focus-footer">
              <button
                type="button"
                className="focus-btn"
                onClick={() => setStage({ kind: "createTask" })}
              >
                {location.trim() === ""
                  ? t("focus.create.location.skip")
                  : t("focus.create.location.next")}
              </button>
              <span className="focus-enter-hint">
                {t("focus.enterHint.press")} <kbd>⌘/Ctrl+Enter</kbd>
              </span>
            </div>
          </>
        )}
      </FocusFrame>
    );
  }

  if (stage.kind === "createTask") {
    return (
      <FocusFrame
        stepKey="createTask"
        progress={0.24}
        // No-map projects skip the location step, so Back goes to intro; every
        // other case returns to the location step (panel or free text).
        onBack={() =>
          setStage({
            kind: mapState.status === "none" ? "intro" : "createLocation",
          })
        }
      >
        <Kicker num={t("focus.create.task.num")}>{t("focus.create.kicker")}</Kicker>
        <Headline>{t("focus.create.task.headline")}</Headline>
        <Help>{t("focus.create.task.help")}</Help>
        {/* Step 1's answer, carried onto step 2. Rendered from the NODE IDS at
            the viewer's locale (never from `location`, which holds the canonical
            answer bytes in the map's own language), with a Change that re-opens
            the panel on the same leaf. A free-text location shows the text the
            user typed, which needs no translation. */}
        {chosenLocationLabel !== null && (
          <p className="pk-advisory">
            {t("focus.create.task.chosenLocation", { path: chosenLocationLabel })}{" "}
            <button
              type="button"
              className="focus-link"
              onClick={() => setStage({ kind: "createLocation" })}
            >
              {t("focus.create.task.changeLocation")}
            </button>
          </p>
        )}
        <AutoGrowTextarea
          className="focus-field is-box"
          autoFocus
          minLines={4}
          aria-label={t("focus.create.task.aria")}
          placeholder={t("focus.create.task.placeholder")}
          value={task}
          onChange={(e) => setTask(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
              e.preventDefault();
              void runCreate();
            }
          }}
        />
        {/* A Figma link in the text while Figma is not connected: one quiet
            line with Connect, on THIS step — the issue is filed on submit, before
            any chip exists. */}
        <CreateFigmaHint text={task} />
        {createError && <div className="focus-error">{createError}</div>}
        <NextButton
          onClick={() => void runCreate()}
          disabled={task.trim() === ""}
          label={t("focus.create.task.submit")}
        />
      </FocusFrame>
    );
  }

  // creating
  return (
    <FocusFrame stepKey="creating" progress={0.3}>
      <div className="focus-spinner" />
      <Kicker>{t("focus.create.kicker")}</Kicker>
      <Headline>{t("focus.create.creating.headline")}</Headline>
      {/* The server's own `progress.message` is NOT rendered. It is workflow-
          authored English written for a developer reading a log — live it put
          "Checking required slots" in front of a business owner mid-create, in a
          German session. The PHASE is the honest, localizable fact, so the copy
          comes from the catalog keyed on it; the message stays on the wire for
          the debug surfaces. */}
      <Help>{t(CREATE_PROGRESS_KEY[create.progress?.phase ?? "creating"])}</Help>
    </FocusFrame>
  );
}

/* ─── Refine mount from the URL ──────────────────────────────────────────
 *
 * The single entry into the refine chat, for BOTH the wizard's own navigation
 * and a cold URL open (reload, deep link, browser Back). Re-derives the issue
 * title/webUrl (useIssueDetail, the same fetch IssuePage uses) and restores the
 * original seed from sessionStorage (falling back to a regenerated refine
 * seed), then mounts RefineFlow.
 *
 * Which of the two it is decides whether RefineFlow may send the seed, because
 * the seed is not idempotent — it carries `flow.boundary: true`, which tells the
 * server to CLEAR the flow's CLI conversation and re-base the evidence horizon.
 * The card survives that (it is server-side `CardState`, re-emitted on attach),
 * so a re-seeded reload used to look almost right — green fields, but an agent
 * with no memory, re-asking questions the card already showed answered. The
 * in-memory fresh-entry marker is what tells the two apart; RefineFlow then
 * withholds the seed on a resume until the server proves the flow is gone. */

function RefineResume({
  projectId,
  iid,
  onLeave,
  onProjects,
  onOpenIssue,
  onSwitchIssue,
}: {
  readonly projectId: number;
  readonly iid: number;
  readonly onLeave: () => void;
  /** Back to the project picker — the refine escape screens' way out. */
  readonly onProjects: () => void;
  readonly onOpenIssue: (iid: number) => void;
  /** Go back to another issue this project session is mid-flow on — the
   *  parked-flow notice's way back. Must be the router-level navigation, which
   *  sets no fresh marker, so the flow re-enters RESUMED and never re-seeds. */
  readonly onSwitchIssue: (iid: number) => void;
}) {
  const t = useT();
  const { data, loading, error } = useIssueDetail(projectId, iid);

  // The CHAT slot's tab-close detach, on the half of the surface `ProjectFocus`
  // does not cover: a refine URL renders this instead, and RefineFlow below
  // beacons only its own issue's binding. A tab that chatted and then
  // opened an issue holds both sandboxes, and the one no surface here is
  // standing in still has to enter the server's detach grace rather than being
  // left to a hard reap. Nothing arms it — the chat is not the stage.
  const chatSessionId = useProjectSession(projectId, "chat").sessionId;
  useSessionPagehideBeacon(chatSessionId);

  // Bumped when the flow asks for a FRESH SESSION on the SAME issue (the budget
  // wall). It rides RefineFlow's `key`, so the flow is torn down and rebuilt —
  // new `useProjectSession` read (the binding was just evicted, so `ensure()`
  // cold-boots), empty local state — without leaving this URL. Leaving it is what
  // the old button did: it navigated back to the project and dropped the user on
  // the mode picker, abandoning the issue they were mid-refine on.
  const [freshEpoch, setFreshEpoch] = useState(0);

  // Read the marker ONCE per mount, before any of the branches below can bail
  // out — `useIssueDetail` renders a loading branch first, and the answer must
  // not change under RefineFlow once it is mounted. The read is a pure lookup,
  // so re-initialising this ref on a discarded render pass is harmless: the pass
  // that commits reads the same marker and gets the same answer.
  const freshRef = useRef<boolean | null>(null);
  if (freshRef.current === null)
    freshRef.current = peekFreshRefineEntry(projectId, iid);

  // Burn it in the COMMIT phase. This is what makes the marker good for one
  // mount rather than one render: the wizard navigation that set it is spent
  // here, and any later mount on the same URL (Back, a re-opened link) reads
  // "resume" and leaves the agent's conversation alone.
  useEffect(() => {
    consumeFreshRefineEntry(projectId, iid);
  }, [projectId, iid]);

  // Stable seed: the persisted original (so a create-originated reload
  // byte-matches RefineFlow's seed fence) or a regenerated refine seed once the
  // title is known. A legacy row carries no span, so `evidence` stays undefined
  // and that one send declares none — see `readSeed`.
  const seed = useMemo(
    () => readSeed(projectId, iid) ?? makeRefineSeed(iid, data?.issue.title ?? ""),
    [projectId, iid, data?.issue.title],
  );

  if (error) {
    return (
      <FocusFrame
        stepKey="refineResumeError"
        progress={0.16}
        onBack={onLeave}
      >
        <Headline>{t("focus.refine.resume.errorHeadline")}</Headline>
        <div className="focus-error">{error}</div>
        <div className="focus-footer">
          <button type="button" className="focus-btn" onClick={onLeave}>
            {t("focus.refine.resume.back")}
          </button>
        </div>
      </FocusFrame>
    );
  }

  if (loading || !data) {
    return (
      <FocusFrame stepKey="refineResumeLoading" progress={0.16}>
        <div className="focus-spinner" />
        <Headline>{t("focus.refine.resume.loading")}</Headline>
      </FocusFrame>
    );
  }

  return (
    <RefineFlow
      key={`${iid}:${freshEpoch}`}
      projectId={projectId}
      iid={iid}
      issueTitle={data.issue.title}
      issueWebUrl={data.issue.webUrl}
      seed={seed.text}
      seedEvidence={seed.evidence}
      // A fresh-session remount IS a fresh entry by construction: the session it
      // would have resumed no longer exists (the flow evicted it before asking
      // for this), so the new mount must seed at once rather than sit out the
      // resume grace waiting for a flow the server cannot have.
      resumed={freshEpoch === 0 ? !freshRef.current : false}
      // What GitLab holds for this issue TODAY, for the closed-choice steps.
      // Already fetched — `useIssueDetail` returns the whole projected issue —
      // and it is the ONLY source for it: an agent question is a `needs_input`
      // slot, which carries no value, so without this the priority step could
      // mark nothing while the agent's own question invited keeping the current
      // value.
      //
      // `priority` is read from the LABELS, not from `issue.priority`: the
      // projection defaults an unlabelled issue to "normal" (`derivePriority`),
      // and marking "Normal" as the issue's current value would invent one.
      issueCurrent={{
        priority: data.issue.labels.some((l) => l.startsWith("priority::"))
          ? data.issue.priority
          : null,
        milestoneTitle: data.issue.milestone?.title ?? null,
      }}
      onProjects={onProjects}
      onRestart={onLeave}
      onOpenIssue={onOpenIssue}
      onSwitchIssue={onSwitchIssue}
      onFreshSession={() => setFreshEpoch((n) => n + 1)}
    />
  );
}

/* ─── Inside a project: the two surfaces plus the history rail ───────────
 *
 * Owns the pick machine because it owns BOTH landing surfaces: a picked session
 * lands on refine (a URL change) or on a `ProjectFocus` stage, and only a
 * component above both can reach either.
 *
 * A pick lands by REMOUNTING the surface. The adopted id lives in a
 * module-scope cache read by `useState` initializers, and the refine flow's
 * decisions (`seededRef`, `flowAliveRef`, `resumeDeadlineRef`) are mount-scoped
 * — so the rebuilt surface re-reads the binding and the previous session's
 * evidence cannot decide the picked one's fate. Two mechanisms do that
 * remount, for two different shapes of pick:
 *
 *   - `adoptEpoch` rides the child `key`, for a pick that does NOT change the
 *     route (a refine URL that stays refine, a stage picked off a stage);
 *   - the ROUTE ITSELF, for a pick that crosses between them. The Focus routes
 *     are siblings with different component functions, so a route change
 *     unmounts this component — which is why the landing stage is claimed
 *     through `claimStageHandoff` (module scope, read-once) and not held here.
 */

function ProjectSurface({
  projectId,
  refineIid,
  onBackToProjects,
  onEnterRefine,
  onLeaveRefine,
  onOpenIssue,
  launchIssueIid,
}: {
  readonly projectId: number;
  readonly refineIid: number | null;
  readonly launchIssueIid?: number;
  readonly onBackToProjects: () => void;
  readonly onEnterRefine: (iid: number) => void;
  readonly onLeaveRefine: () => void;
  readonly onOpenIssue: (projectId: number, iid: number) => void;
}) {
  const [adoptEpoch, setAdoptEpoch] = useState(0);

  // Which of the project's two bindings the surface behind the drawer stands in.
  // `ProjectFocus` owns the stage and reports it; a refine URL renders
  // `RefineResume` instead, which stands in the issue slot by definition — so
  // the URL overrides a stale report left by the stage the user just left.
  const [stageIsChat, setStageIsChat] = useState(false);
  const chatStageActive = refineIid === null && stageIsChat;

  // A refusal never reaches here: nothing was adopted and nothing navigates, so
  // the pick machine owns it and the rail is the surface that explains it.
  const {
    pick,
    pendingSessionId,
    refusal,
    refusedSessionId,
    clearRefusal,
    retry,
  } = useFocusSessionPick(projectId, chatStageActive, (target) => {
    // The refine landing. The rail lists chats, and a row whose derived flow
    // says otherwise is refused at the pick — so this branch is the description
    // of what reopening an issue conversation means, held ready for when that
    // is offered again, rather than a path a listed row reaches today.
    if (target.kind === "refine") {
      setAdoptEpoch((n) => n + 1);
      // The ROUTER-level prop, deliberately not ProjectFocus's own
      // `enterRefine`: that one marks a FRESH entry, and a fresh entry seeds
      // at once — which on an existing conversation is the boundary send that
      // wipes it. Without the marker `RefineResume` mounts `resumed`, so the
      // flow waits for the replay to prove the server still holds it.
      onEnterRefine(target.iid);
      return;
    }
    // Chat and implement both land on a `ProjectFocus` stage. The claim goes
    // through the module handoff rather than this component's state because
    // the branch below can navigate away from the refine URL, which tears
    // THIS component down before the stage could be read off it.
    claimStageHandoff(
      projectId,
      target.kind === "implement"
        ? {
            kind: "implement",
            iid: target.iid,
            title: target.title,
            webUrl: target.webUrl,
            resumeSessionId: target.resumeSessionId,
          }
        : { kind: "chat" },
    );
    // A stage only renders off a refine URL. Leaving one remounts the whole
    // surface, which consumes the handoff; staying on one needs the epoch to
    // remount `ProjectFocus` under an unchanged route.
    if (refineIid !== null) onLeaveRefine();
    else setAdoptEpoch((n) => n + 1);
  });

  // "New chat" from the rail. It lands the SAME way a pick does — clear the
  // binding, claim the chat stage, remount — and it has to, because the rail is
  // offered on surfaces where `ProjectFocus` is not mounted at all (a refine
  // URL renders `RefineResume`). A callback reported upward from the stage
  // would be missing on exactly those.
  //
  // That remount is also what keeps this correct without a third
  // `useProjectSession` instance here: `ProjectFocus` and `FocusChat` each hold
  // one over a shared module cache with no subscription between them, so a
  // binding moved out from under a LIVE instance goes stale. Rebuilding the
  // subtree re-reads the (now empty) binding in both, and the pre-warm mints one
  // fresh session that both instances then see.
  //
  // `ProjectFocus.startFreshChat` stays as it is and is NOT replaced by this:
  // it fires from inside a mounted chat (the budget wall, the ended block),
  // where this component does not re-render and its own hook state is what the
  // pagehide beacon reads.
  const startNewChatFromRail = useCallback(() => {
    // `?reason=pagehide`, never the bare DELETE — that one stamps the write-once
    // `endedAt` latch, and the chat being left is healthy and must stay listed.
    const outgoing = readProjectSessionId(projectId, "chat");
    if (outgoing !== null) detachSession(outgoing);
    // Cache + inflight + generation bump + storage. The generation bump is what
    // makes an in-flight `POST /sessions` detach itself rather than land on a
    // binding this click just moved.
    clearProjectSessionBinding(projectId, "chat");
    claimStageHandoff(projectId, { kind: "chat" });
    if (refineIid !== null) onLeaveRefine();
    else setAdoptEpoch((n) => n + 1);
  }, [projectId, refineIid, onLeaveRefine]);

  const rail = (
    <FocusSessionRail
      projectId={projectId}
      chatStageActive={chatStageActive}
      onPick={pick}
      onNewChat={startNewChatFromRail}
      pendingSessionId={pendingSessionId}
      refusal={refusal}
      refusedSessionId={refusedSessionId}
      onDismissRefusal={clearRefusal}
      onRetry={retry}
    />
  );

  // Refine is URL-reflected: a refine URL renders the chat (resumed from its own
  // session + the re-fetched issue) regardless of how we got here.
  if (refineIid !== null) {
    return (
      <FocusRailProvider rail={rail}>
        <RefineResume
          key={`${projectId}:${refineIid}:${adoptEpoch}`}
          projectId={projectId}
          iid={refineIid}
          onLeave={onLeaveRefine}
          onProjects={onBackToProjects}
          onOpenIssue={(iid) => onOpenIssue(projectId, iid)}
          // The ROUTER-level prop, deliberately not `ProjectFocus`'s own
          // `enterRefine` — the same distinction the pick landing makes above,
          // and for the same reason: `enterRefine` marks a FRESH entry, a fresh
          // entry seeds at once, and the seed carries `boundary: true`, which
          // would wipe the very conversation this button promises to return to.
          onSwitchIssue={onEnterRefine}
        />
      </FocusRailProvider>
    );
  }

  return (
    <FocusRailProvider rail={rail}>
      <ProjectFocus
        key={`${projectId}:${adoptEpoch}`}
        projectId={projectId}
        onBackToProjects={onBackToProjects}
        onOpenIssue={(iid) => onOpenIssue(projectId, iid)}
        onEnterRefine={onEnterRefine}
        onChatStageChange={setStageIsChat}
        {...(launchIssueIid !== undefined ? { launchIssueIid } : {})}
      />
    </FocusRailProvider>
  );
}

/* ─── Page ───────────────────────────────────────────────────────────── */

export function FocusPage({
  projectId,
  refineIid,
  onPickProject,
  onBackToProjects,
  onEnterRefine,
  onLeaveRefine,
  onOpenIssue,
  launchIssueIid,
}: FocusPageProps) {
  // No project, no rail: every session-cache key is a projectId, so a
  // cross-project pick would have to navigate first and adopt second. The
  // picker screen renders without a rail provider, so `FocusFrame` reads the
  // context default (null) and adds neither toggle nor drawer.
  if (projectId === null) {
    return (
      <ProjectStep onPick={(p) => onPickProject(p.id)} />
    );
  }

  return (
    <ProjectSurface
      projectId={projectId}
      refineIid={refineIid}
      onBackToProjects={onBackToProjects}
      onEnterRefine={onEnterRefine}
      onLeaveRefine={onLeaveRefine}
      onOpenIssue={onOpenIssue}
      {...(launchIssueIid !== undefined ? { launchIssueIid } : {})}
    />
  );
}
